/**
 * @fileoverview Cache implementation for CRUD data sources with TTL support.
 */

export interface DataSourceCacheConfig {
  /**
   * Time To Live for each cache entry in milliseconds.
   * After this time the cache entry will become stale and the next query will result in cache miss.
   * @default 300000 (5 minutes)
   */
  ttl?: number;
}

/**
 * Cache entry with value and expiration timestamp.
 */
interface CacheEntry<T> {
  value: T;
  expiry: number;
}

/**
 * Type-safe cache for data source operations.
 * Stores cached responses keyed by serialized request parameters.
 */
export class DataSourceCache {
  private cache: Map<string, CacheEntry<unknown>>;
  private readonly ttl: number;

  constructor(config?: DataSourceCacheConfig) {
    this.cache = new Map();
    this.ttl = config?.ttl ?? 300000;
  }

  /**
   * Stores a value in the cache with automatic TTL expiration.
   * @param key - Cache key (typically JSON-serialized request params)
   * @param value - Value to cache
   */
  set<T>(key: string, value: T): void {
    const expiry = Date.now() + this.ttl;
    this.cache.set(key, { value, expiry });
  }

  /**
   * Retrieves a value from cache if it exists and hasn't expired.
   * @param key - Cache key to look up
   * @returns The cached value or undefined if not found/expired
   */
  get<T>(key: string): T | undefined {
    const entry = this.cache.get(key);
    if (!entry) {
      return undefined;
    }

    if (Date.now() > entry.expiry) {
      this.cache.delete(key);
      return undefined;
    }

    return entry.value as T;
  }

  /**
   * Checks if a key exists in the cache and hasn't expired.
   */
  has(key: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) {
      return false;
    }
    if (Date.now() > entry.expiry) {
      this.cache.delete(key);
      return false;
    }
    return true;
  }

  /**
   * Removes a single entry from the cache.
   * @param key - Cache key to remove
   * @returns true if an entry was removed, false if key didn't exist
   */
  delete(key: string): boolean {
    return this.cache.delete(key);
  }

  /**
   * Clears all entries from the cache.
   */
  clear(): void {
    this.cache = new Map();
  }

  /**
   * Returns the number of entries currently in the cache (including expired).
   */
  get size(): number {
    return this.cache.size;
  }
}
